import { useState } from "react";
import { useSelector } from "react-redux";
import moment from "moment-jalaali";
import { Calendar, Trash2, AlertCircle, CheckCircle, ChevronLeft, ChevronRight } from "lucide-react";
import { toast } from "react-toastify";

const StaffHolidayTab = ({ formData, setFormData }) => {
  const serverTimeData = useSelector((state) => state.info.serverTime);
  
  // serverTime یه آبجکت هست: { day: {...}, serverTime: "...", timestamp: ... }
  const serverTimeISO = serverTimeData?.serverTime || new Date().toISOString();
  
  const currentJalaaliYear = moment(serverTimeISO).jYear();
  const currentJalaaliMonth = moment(serverTimeISO).jMonth() + 1;
  
  const [selectedYear, setSelectedYear] = useState(currentJalaaliYear);
  const [selectedMonth, setSelectedMonth] = useState(currentJalaaliMonth);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const persianMonths = [
    { id: 1, name: "فروردین", days: 31 },
    { id: 2, name: "اردیبهشت", days: 31 },
    { id: 3, name: "خرداد", days: 31 },
    { id: 4, name: "تیر", days: 31 },
    { id: 5, name: "مرداد", days: 31 },
    { id: 6, name: "شهریور", days: 31 },
    { id: 7, name: "مهر", days: 30 },
    { id: 8, name: "آبان", days: 30 },
    { id: 9, name: "آذر", days: 30 },
    { id: 10, name: "دی", days: 30 },
    { id: 11, name: "بهمن", days: 30 },
    { id: 12, name: "اسفند", days: 29 },
  ];

  const weekDays = ["ش", "ی", "د", "س", "چ", "پ", "ج"];

  const isLeapYear = (year) => {
    const breaks = [1, 5, 9, 13, 17, 22, 26, 30];
    return breaks.includes(year % 33);
  };

  const getDaysInMonth = (month, year) => {
    if (!month || month < 1 || month > 12) return 30;
    if (month === 12 && isLeapYear(year)) return 30;
    return persianMonths[month - 1].days;
  };

  const generateCalendarDays = () => {
    if (!selectedMonth || !selectedYear) return [];

    const daysInMonth = getDaysInMonth(selectedMonth, selectedYear);
    const firstDayOfMonth = moment(`${selectedYear}/${selectedMonth}/1`, "jYYYY/jM/jD");
    const startDayOfWeek = firstDayOfMonth.day() + 1;

    const days = [];

    for (let i = 0; i < startDayOfWeek; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      const jalaaliDate = moment(`${selectedYear}/${selectedMonth}/${day}`, "jYYYY/jM/jD");
      const gregorianDate = jalaaliDate.format("YYYY-MM-DD");

      days.push({
        day,
        jalaaliDate: jalaaliDate.format("jYYYY/jMM/jDD"),
        gregorianDate: `${gregorianDate}T00:00:00.000Z`,
        isToday: jalaaliDate.isSame(moment(serverTimeISO), "day"),
      });
    }

    return days;
  };

  const calendarDays = generateCalendarDays();

  const isDateSelected = (gregorianDate) => {
    return formData.holidays?.includes(gregorianDate) || false;
  };

  const toggleDateSelection = (dateObj) => {
    if (!dateObj) return;
    const { gregorianDate, jalaaliDate } = dateObj;

    if (isDateSelected(gregorianDate)) {
      setConfirmDelete({ gregorianDate, jalaaliDate });
    } else {
      setFormData((prev) => ({
        ...prev,
        holidays: [...(prev.holidays || []), gregorianDate].sort(),
      }));
      toast.success(`${jalaaliDate} به تعطیلات اضافه شد`, { className: "z-[10001]" });
    }
  };

  const confirmDeleteAction = () => {
    if (!confirmDelete) return;
    const { gregorianDate, jalaaliDate } = confirmDelete;

    setFormData((prev) => ({
      ...prev,
      holidays: (prev.holidays || []).filter((date) => date !== gregorianDate),
    }));

    toast.success(`${jalaaliDate} از تعطیلات حذف شد`, { className: "z-[10001]" });
    setConfirmDelete(null);
  };

  const changeMonth = (direction) => {
    if (direction === "next") {
      if (selectedMonth === 12) { setSelectedMonth(1); setSelectedYear(selectedYear + 1); }
      else setSelectedMonth(selectedMonth + 1);
    } else {
      if (selectedMonth === 1) { setSelectedMonth(12); setSelectedYear(selectedYear - 1); }
      else setSelectedMonth(selectedMonth - 1);
    }
  };

  const getSelectedDatesInPersian = () => {
    if (!formData.holidays?.length) return [];
    return formData.holidays
      .map((gregorianDate) => ({
        jDate: moment(gregorianDate).format("jYYYY/jMM/jDD"),
        gregorianDate,
      }))
      .sort((a, b) => a.jDate.localeCompare(b.jDate));
  };

  const currentMonthName =
    selectedMonth >= 1 && selectedMonth <= 12
      ? persianMonths[selectedMonth - 1].name
      : "نامشخص";

  return (
    <div className="space-y-6">
      {/* راهنما */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Calendar className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="text-sm text-blue-800">
          <p className="font-medium mb-1">راهنمای تنظیم تعطیلات:</p>
          <ul className="list-disc list-inside space-y-1 text-blue-700">
            <li>روی هر روز کلیک کنید تا به عنوان تعطیلی انتخاب شود</li>
            <li>روزهای انتخاب شده با رنگ قرمز مشخص می‌شوند</li>
            <li>برای حذف، دوباره روی روز کلیک کنید</li>
            <li>تقویم بر اساس سال جاری سرور ({currentJalaaliYear}) نمایش داده می‌شود</li>
          </ul>
        </div>
      </div>

      {/* هدر تقویم */}
      <div className="bg-gradient-to-br from-purple-600 to-purple-700 rounded-xl p-5 shadow-lg shadow-purple-500/25">
        <div className="flex items-center justify-between mb-4">
          <button
            onClick={() => changeMonth("prev")}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group"
          >
            <ChevronRight className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
          </button>

          <div className="text-center">
            <h3 className="text-2xl font-bold text-white">
              {currentMonthName} {selectedYear}
            </h3>
            <p className="text-purple-100 text-sm mt-1">
              {isLeapYear(selectedYear) ? "سال کبیسه" : "سال عادی"}
            </p>
          </div>

          <button
            onClick={() => changeMonth("next")}
            className="w-10 h-10 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group"
          >
            <ChevronLeft className="w-5 h-5 text-white group-hover:scale-110 transition-transform" />
          </button>
        </div>

        <div className="grid grid-cols-7 gap-2 mb-2">
          {weekDays.map((day, index) => (
            <div key={index} className="text-center text-white/90 font-semibold text-sm py-2">
              {day}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-7 gap-2">
          {calendarDays.map((dateObj, index) => {
            if (!dateObj) return <div key={`empty-${index}`} className="aspect-square" />;

            const isSelected = isDateSelected(dateObj.gregorianDate);
            const isToday = dateObj.isToday;

            return (
              <button
                key={index}
                onClick={() => toggleDateSelection(dateObj)}
                className={`
                  aspect-square rounded-lg font-medium text-sm transition-all duration-300 relative
                  ${isSelected
                    ? "bg-red-500 text-white shadow-lg shadow-red-500/50 scale-105"
                    : isToday
                    ? "bg-white/20 text-white border-2 border-white/50"
                    : "bg-white/10 text-white hover:bg-white/20 hover:scale-105"
                  }
                `}
              >
                {dateObj.day}
                {isToday && !isSelected && (
                  <div className="absolute bottom-1 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full bg-white" />
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* لیست تعطیلات */}
      {formData.holidays && formData.holidays.length > 0 ? (
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <h3 className="text-base font-bold text-slate-800 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            تعطیلات انتخاب شده ({formData.holidays.length} روز)
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {getSelectedDatesInPersian().map(({ jDate, gregorianDate }, index) => (
              <div
                key={index}
                className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center justify-between group hover:shadow-md transition-shadow duration-300"
              >
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 rounded-lg bg-red-500 flex items-center justify-center">
                    <Calendar className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm font-medium text-slate-700 font-mono"> {moment(Number(new Date(gregorianDate).getTime() + 86400000)).format("jYYYY/jMM/jDD")}</span>
                </div>
                <button
                  onClick={() => setConfirmDelete({ gregorianDate, jalaaliDate: moment(Number(new Date(gregorianDate).getTime() + 86400000)).format("jYYYY/jMM/jDD") })}
                  className="w-7 h-7 rounded-lg flex items-center justify-center text-slate-400 hover:text-red-600 hover:bg-red-100 transition-all duration-300 sm:opacity-0 group-hover:opacity-100"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      ) : (
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-10 text-center">
          <Calendar className="w-12 h-12 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">هنوز تعطیلی انتخاب نشده است</p>
          <p className="text-slate-400 text-xs mt-1">روی روزهای تقویم کلیک کنید تا به عنوان تعطیلی ثبت شود</p>
        </div>
      )}

      {/* مودال تایید حذف */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[10000] p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 animate-in fade-in zoom-in duration-300">
            <div className="w-14 h-14 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-4">
              <AlertCircle className="w-7 h-7 text-red-600" />
            </div>
            <h3 className="text-lg font-bold text-slate-800 text-center mb-2">حذف تعطیلی</h3>
            <p className="text-sm text-slate-600 text-center mb-6">
              آیا از حذف تاریخ{" "}
              <span className="font-bold text-slate-800 font-mono">{confirmDelete.jalaaliDate}</span>{" "}
              از لیست تعطیلات اطمینان دارید؟
            </p>
            <div className="flex items-center gap-3">
              <button
                onClick={() => setConfirmDelete(null)}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 rounded-lg transition-colors duration-300"
              >
                انصراف
              </button>
              <button
                onClick={confirmDeleteAction}
                className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 rounded-lg shadow-md shadow-red-500/25 hover:shadow-lg hover:shadow-red-500/30 transition-all duration-300"
              >
                حذف کن
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaffHolidayTab;